<template>
    <q-layout view="hHh Lpr lFf" class="fit" @contextmenu="addContextMenuItems({items:defaultContextMenuItems, $event})">
        <!-- ───────────── HEADER ───────────── -->
        <q-header class="kiplan-header">
            <q-toolbar class="kiplan-toolbar">

                <!-- Drawer toggle -->
                <q-btn flat round dense icon="menu" class="text-white menu-btn" @click="drawer = !drawer" />

                <!-- Logo — brand centrepiece -->
                <q-toolbar-title class="flex ">
                    <img 
                        class="header-logo"
                        :src="homeLogoUrl"
                        style="height: 28px; width: auto;"
                        @click="redirect('/')"
                    />
                </q-toolbar-title>

                <!-- Spacer so logo stays centred -->
                <div style="width:40px" />

                <!-- Notifications -->
                <q-btn flat round dense class="text-white" @click="notificationsDrawer = !notificationsDrawer">
                    <q-icon name="notifications" />
                    <q-badge v-if="$refs?.notifications?.num_notifications > 0" color="red" floating>
                        {{ $refs?.notifications?.num_notifications }}
                    </q-badge>
                    <q-tooltip>{{ $t('menu.notifications') }}</q-tooltip>
                </q-btn>

                <!-- Settings button -->
                <q-btn flat round dense icon="settings" class="text-white">
                    <q-tooltip>{{ $t('menu.settings') }}</q-tooltip>
                    <q-menu anchor="bottom right" self="top right" :offset="[0, 8]" style="min-width:190px">
                        <q-list dense padding>
                            <q-item v-if="user.is_superuser" clickable v-ripple v-close-popup @click="openNewTab('/admin')">
                                <q-item-section avatar><q-icon name="manage_accounts" /></q-item-section>
                                <q-item-section>{{ $t('menu.administration') }}</q-item-section>
                            </q-item>
                            <q-item clickable v-ripple v-close-popup @click="redirect('/apps/company')">
                                <q-item-section avatar><q-icon name="business" /></q-item-section>
                                <q-item-section>{{ $t('menu.company') }}</q-item-section>
                            </q-item>
                            <q-item clickable v-ripple v-close-popup @click="redirect('/apps/security')">
                                <q-item-section avatar><q-icon name="security" /></q-item-section>
                                <q-item-section>{{ $t('menu.security') }}</q-item-section>
                            </q-item>
                            <q-item clickable v-ripple v-if="user.is_superuser">
                                <q-item-section avatar><q-icon name="people" /></q-item-section>
                                <q-item-section>{{ $t('menu.testUser') }}</q-item-section>
                                <q-item-section side>
                                    <q-icon name="chevron_right" />
                                </q-item-section>

                                <q-menu anchor="top end" self="top start">
                                    <div style="display: flex; flex-direction: column;">
                                        <div style="padding: 8px; background: white; position: sticky; top: 0; z-index: 1; border-bottom: 1px solid #e0e0e0;">
                                            <q-input dense outlined v-model="userSearch" :placeholder="$t('menu.searchUser')" clearable autofocus :loading="testUserLoading">
                                                <template v-slot:prepend>
                                                    <q-icon name="search" />
                                                </template>
                                            </q-input>
                                        </div>
                                        <q-scroll-area style="height: 300px;">
                                            <q-list dense>
                                                <q-item
                                                    v-for="u in filteredUsers"
                                                    :key="u.id"
                                                    clickable v-ripple
                                                    @click="test_user(u.id)"
                                                >
                                                    <q-item-section>
                                                        <q-item-label>{{ u.username }}</q-item-label>
                                                        <q-item-label caption>{{ u.id }} - {{ u.email }}</q-item-label>
                                                    </q-item-section>
                                                </q-item>
                                            </q-list>
                                        </q-scroll-area>
                                    </div>
                                </q-menu>
                            </q-item>
                        </q-list>
                    </q-menu>
                </q-btn>

                <!-- Profile button with dropdown -->
                <q-btn flat round dense icon="account_circle" class="text-white">
                    <q-tooltip>{{ $t('menu.profile') }}</q-tooltip>
                    <q-menu anchor="bottom right" self="top right" :offset="[0, 8]" style="min-width:180px">
                        <q-list dense padding>
                            <q-item clickable v-ripple v-close-popup @click="redirect('/apps/ConfigProfile')">
                                <q-item-section avatar><q-icon name="person" /></q-item-section>
                                <q-item-section>{{ $t('menu.myProfile') }}</q-item-section>
                            </q-item>
                            <q-item v-if="helpdeskEnabled" clickable v-ripple v-close-popup @click="redirect('/apps/nomenu/helpdesk')">
                                <q-item-section avatar><q-icon name="support_agent" /></q-item-section>
                                <q-item-section>{{ $t('menu.support') }}</q-item-section>
                            </q-item>
                            <q-separator />
                            <q-item clickable v-ripple v-close-popup @click="logout">
                                <q-item-section avatar><q-icon name="logout" color="red-4" /></q-item-section>
                                <q-item-section class="text-red-4">{{ $t('menu.logout') }}</q-item-section>
                            </q-item>
                        </q-list>
                    </q-menu>
                </q-btn>
            </q-toolbar>
        </q-header>

        <!-- ───────────── LEFT DRAWER ───────────── -->
        <q-drawer
            v-model="drawer"
            side="left"
            :width="240"
            behavior="desktop"
            bordered
            class="bg-primary text-white"
            @hide="triggerResize"
            @show="triggerResize"
        >
            <div class="column full-height">

                <!-- User info -->
                <div class="drawer-top q-pa-md row items-center">
                    <q-avatar size="56px" class="q-mr-md">
                        <img src="https://cdn.prod.website-files.com/61ec4f2dd6418377ab08a316/6977aabaa042408517ad7d54_iconokiplan216.png" />
                    </q-avatar>
                    <div class="column user-info">
                        <span class="text-weight-bold text-white user-name">{{ user.username }}</span>
                        <q-badge color="secondary" outline class="q-mt-xs user-env">{{ user.company_name }}</q-badge>
                    </div>
                </div>

                <!-- Menu -->
                <div class="col column no-wrap">
                    <div class="drawer-section-title text-secondary q-px-md q-pt-md q-pb-sm">{{ $t('menu.modules') }}</div>
                    <q-scroll-area class="col">
                        <div v-if="!menuItems" class="row justify-center q-pa-md">
                            <q-spinner color="white" />
                        </div>
                        <q-tree
                            v-else 
                            :nodes="processedMenuItems"
                            node-key="id"
                            label-key="name"
                            :selected="treeState.selected"
                            v-model:expanded="treeState.expanded"
                            selected-color="secondary"
                            dark
                            @update:selected="onSelect"
                            @contextmenu="user.is_superuser ? addContextMenuItems({items: contextMenuItems, priority: 10, $event}) : null"
                        >
                        </q-tree>
                    </q-scroll-area>
                </div>

            </div>
        </q-drawer>

        <!-- ───────────── NOTIFICATIONS DRAWER ───────────── -->
        <q-drawer v-model="notificationsDrawer" side="right" bordered :width="350" class="bg-grey-1 shadow-5">
            <q-toolbar class="bg-primary text-white">
                <q-toolbar-title>{{ $t('menu.notifications') }}</q-toolbar-title>
                <q-btn flat icon="close" dense @click="notificationsDrawer = false" />
            </q-toolbar>
            <NotificationsComp ref="notifications" />
        </q-drawer>

        <!-- ───────────── MAIN CONTENT ───────────── -->
        <q-page-container>
            <q-page>
                <router-view :key="$route.fullPath" />
            </q-page>
        </q-page-container>

    <q-dialog v-model="modifyMenyDialog" transition-show="slide-up" transition-hide="slide-down">
        <q-card style="width: 1200px; max-width: 85vw; border-radius: 16px; overflow: hidden; box-shadow: 0 24px 80px rgba(0,0,0,0.35);">
            <q-card-section class="row items-center q-pb-sm" style="background: linear-gradient(135deg, #1a1a2e, #16213e); padding: 20px 28px;">
                <div class="text-h5 text-white q-ml-sm" style="font-weight: 600;">{{ $t('menu.modifyMenu') }}</div>
                <q-icon name="menu" color="amber-4" size="28px" class="q-ml-sm" />
                <q-space />
                <q-btn icon="close" flat round dense color="grey-5" v-close-popup />
            </q-card-section>
            <q-separator />
            <q-card-section style="padding: 24px 28px; overflow-y: auto;">
                <MenuEditor :rootId="menu_root" @menu-updated="getMenuForUser(menu_root)" />
            </q-card-section>
        </q-card>
        </q-dialog>
    </q-layout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { Auth } from '@/database'
import NotificationsComp from '@/components/NotificationsComp.vue'
import MenuEditor from './MenuEditor.vue'
export default {
    components: { NotificationsComp, MenuEditor },

    data() {
        return {
            development: process.env.NODE_ENV === 'development',
            drawer: (sessionStorage.getItem('show_kiplan_menu') || 'true') === 'true',
            activeItem: null,
            menuItems: JSON.parse(localStorage.getItem('kiplan_menu') || 'null'),
            treeState: {
                expanded: [],
                selected: null,
            },
            notificationsDrawer: false,
            menu_root: 1000,
            homeLogoUrl: new URL('@/assets/home_logo.png', import.meta.url).href,
            userSearch: '',
            testUserLoading: false,
            modifyMenyDialog: false,
            helpdeskEnabled: false,
        }
    },
    mounted() {
        this.$el.removeAttribute('tabindex') // !!! IMPORTANT for right-click to work without focusing the drawer first
    },
    async created() {
        this.api = new Auth()
        if (this.menuItems) this.selectCurrentRoute()
        await this.getMenuForUser(this.menu_root, false)
        if (!this.users.is_superuser) await this.getUsers()
        this.api.helpdeskConfig().then(config => { this.helpdeskEnabled = config.enabled }).catch(() => {})
    },
    computed: {
        ...mapGetters(['user', 'users']),
        processedMenuItems() {
            const processMenuItem = (item) => {
                if (item.icon && item.icon.startsWith('bi ')) item.icon = null
                if (item.hidden) return {}
                return ({
                    ...item,
                    icon: item.icon || this.defaultIcon(item),
                    children: item.children 
                        ? item.children.map(child => processMenuItem(child))
                        : []
                });
            }
            
            return this.menuItems.map(item => processMenuItem(item));
        },
        filteredUsers() {
            return this.users.filter(user => (user?.username || '').includes(this.userSearch) || (user?.email || '').includes(this.userSearch) || String(user?.id || '').includes(this.userSearch))
        },
        defaultContextMenuItems() {
            return [
                { label: this.$t('menu.embeddedView'), action: () => { this.$router.push(this.$route.path.replace(/^\/apps\//, '/embedded/')) } },
            ]
        },
        contextMenuItems() {
            const items = [
                { label: this.$t('menu.editMenu'), action: () => { this.modifyMenyDialog = true } },
            ]
            if (this.menu_root === 1000) {
                items.push({ label: this.$t('menu.configMenu'), action: () => { this.menu_root = 6000 } })
            } else {
                items.push({ label: this.$t('menu.mainMenu'), action: () => { this.menu_root = 1000 } })
            }
            return items
        },
    },
    methods: {
        ...mapActions(['viewMe', 'getUsers', 'addContextMenuItems']),
        onSelect(val) {
            const node = this.findNode(this.menuItems, val)
            if (node && node.url) {
                this.treeState.selected = val
                if (node.url.startsWith('http://') || node.url.startsWith('https://')) {
                    window.top.location.href = node.url
                } else {
                    this.$router.push(node.url)
                }
            } else {
                const idx = this.treeState.expanded.indexOf(val)
                if (idx === -1) this.treeState.expanded.push(val)
                else this.treeState.expanded.splice(idx, 1)
            }
        },
        findNode(nodes, id) {
            for (const n of nodes) {
                if (n.id === id) return n
                if (n.children) {
                const found = this.findNode(n.children, id)
                if (found) return found
                }
            }
            return null
        },

        defaultIcon(item) {
            if (item.icon && item.icon.startsWith('bi ')) item.icon = null
            if (!item.url) return 'library_books'
            if ((item?.url || '').includes('/dashboard/')) return 'analytics'
            if ((item?.url || '').includes('/processexecutor/')) return 'terminal'
            if ((item?.url || '').includes('/queryvisualizer/')) return 'manage_search'
            if ((item?.url || '').includes('/filemanager')) return 'file_open'
            if ((item?.url || '').includes('/processmanager')) return 'terminal'
            if ((item?.url || '').includes('/trained_assistant')) return 'chat'
            return 'circle'
        },
        
        async getMenuForUser(root_id, delete_existing = true) {
            if (delete_existing) this.menuItems = null
            const response = await this.api.fetch(`/menu/getMenuUser?root_id=${root_id}`, { method: 'GET' })
            if (!response.ok) {
                const err = await response.json()
                throw new Error(err.detail || `Error ${response.status}: ${response.statusText}`)
            }
            const data = await response.json()
            this.menuItems = data.menu
            if (root_id === 1000) localStorage.setItem('kiplan_menu', JSON.stringify(this.menuItems))
            this.selectCurrentRoute()
        },


        triggerResize() {
            window.dispatchEvent(new Event('resize'))
        },
        
        logout() {
            localStorage.clear()
            sessionStorage.clear()
            const currentPath = this.$route.fullPath
            if (this.development) { this.$router.push(`/accounts/logout/?next=${encodeURIComponent(currentPath)}`) }
            else window.top.location.href = `/accounts/logout/?next=${encodeURIComponent(currentPath)}`
        },
        openNewTab(url, extraQuery = null) {
            if (extraQuery) url += (url.includes('?') ? '&' : '?') + new URLSearchParams(extraQuery).toString()
            window.open(url, '_blank')
        },
        redirect(path) {
            this.$router.push(path)
        },

        recursiveRun(items, callback) {
            items.forEach(item => {
                callback(item)
                if (item.children) this.recursiveRun(item.children, callback)
            })
        },
        async test_user(userId){
            localStorage.removeItem('kiplan_menu')
            this.testUserLoading = true
            const token = await this.api.fetch(`/generate-token/?id=${userId}`).then(res => res.json())
            this.openNewTab(this.$route.path, { token: token })
            this.testUserLoading = false
        },
        selectCurrentRoute(currentRoute = this.$route) {
            const currentPath = currentRoute.path
            this.recursiveRun(this.menuItems, (item) => {
                if (item.url === currentPath) {
                    this.treeState.selected = item.id
                    let parentId = item.parent_id
                    while (parentId) {
                        this.treeState.expanded.push(parentId)
                        const parent = this.findNode(this.menuItems, parentId)
                        parentId = parent ? parent.parent_id : null
                    }
                }
            })
        }
    },
    watch: {
        drawer(val) {
            sessionStorage.setItem('show_kiplan_menu', val)
        },
        menu_root(val) {
            this.getMenuForUser(val)
        },
        '$route'(to) {
            if (to.meta?.title) window.top.document.title = to.meta.title
            this.treeState.selected = null
            if (this.menuItems) this.selectCurrentRoute(to)
        },
    },
}
</script>

<style scoped>
/* ── Header ─────────────────────────────── */
.kiplan-header {
    background: linear-gradient(135deg, var(--q-primary) 0%, color-mix(in srgb, var(--q-primary) 80%, black) 100%);
    box-shadow: 0 2px 0 0 var(--q-secondary), 0 2px 12px rgba(0,0,0,0.25);
}

.kiplan-toolbar {
    min-height: 56px;
    padding: 0 12px;
}

.header-logo {
    height: 28px;
    width: auto;
    cursor: pointer;
    filter: brightness(0) invert(1);                /* keeps logo white/clean on any bg */
    transition: opacity 0.2s ease;
}
.header-logo:hover { opacity: 0.8; cursor: pointer; }

.menu-btn { opacity: 0.85; }
.menu-btn:hover { opacity: 1; }

/* ── Drawer ──────────────────────────────── */
.drawer-top {
    border-bottom: 1px solid rgba(255,255,255,0.15);
    min-height: 110px;
}

/* ── Transitions ─────────────────────────── */
.q-page-container { transition: padding-left 0.3s ease; }
.q-drawer                 { transition: all 0.3s ease; }
</style>